import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { fmtDate, getInitialMonth } from '../utils/format';
import ConfirmDialog from '../components/ConfirmDialog';
import SearchableSelect from '../components/SearchableSelect';

// Inline "add a person / stakeholder" — lets the logger create a missing contact without
// leaving the Log Work form (the node is created under the selected client).
function AddNodeInline({ clientId, parentId=null, label, onAdded }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  async function add() {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      const r = await api.post(`/clients/${clientId}/nodes`, { name: n, label, parent_id: parentId });
      await qc.invalidateQueries(['clients']);
      toast.success(`${label} added`);
      onAdded(r.data);
      setName(''); setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.error || `Failed to add ${label.toLowerCase()}`);
    } finally { setBusy(false); }
  }
  if (!open) return (
    <button type="button" onClick={()=>setOpen(true)}
      style={{background:'none',border:'none',padding:0,marginTop:5,fontSize:12,color:'var(--accent)',cursor:'pointer',fontWeight:600}}>
      + Add new {label.toLowerCase()}
    </button>
  );
  return (
    <div style={{display:'flex',gap:6,marginTop:6}}>
      <input autoFocus value={name} placeholder={`${label} name`} onChange={e=>setName(e.target.value)}
        onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); add(); } if(e.key==='Escape') setOpen(false); }}
        style={{flex:1,fontSize:13,padding:'6px 10px'}}/>
      <button type="button" className="btn-primary btn-sm" disabled={busy||!name.trim()} onClick={add}>{busy?'Adding…':'Add'}</button>
      <button type="button" className="btn-sm" onClick={()=>{ setOpen(false); setName(''); }}>Cancel</button>
    </div>
  );
}

export default function LogWorkPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({ client_id:'', category_id:'', hierarchy_node_id:'', description:'', quantity:1, work_date:today, is_billable:true });
  const [dup, setDup] = useState(null);
  const [step, setStep] = useState(1);
  const [month, setMonth] = useState(getInitialMonth);
  const isMobile = window.innerWidth < 768;

  // Proof image state
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);
  const proofInputRef = useRef(null);

  // Edit entry state
  const [editModal, setEditModal] = useState(null);
  const [editClientId, setEditClientId] = useState('');
  const [editPersonNodeId, setEditPersonNodeId] = useState('');
  const [editForm, setEditForm] = useState({ description:'', quantity:1, work_date:'', category_id:'', hierarchy_node_id:'' });
  const [editProofFile, setEditProofFile] = useState(null);
  const [editProofPreview, setEditProofPreview] = useState(null);
  const [editRemoveProof, setEditRemoveProof] = useState(false);
  const editProofInputRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [corrModal, setCorrModal] = useState(null);
  const [corrLines, setCorrLines] = useState([{ correction_type_id:'', correction_count:1, reason:'' }]);

  // Listen to global month changes from topbar
  useEffect(() => {
    const h = e => setMonth(e.detail);
    window.addEventListener('monthChange', h);
    return () => window.removeEventListener('monthChange', h);
  }, []);

  // Ctrl+V paste to capture screenshot (new entry form)
  useEffect(() => {
    const handlePaste = e => {
      if (editModal) return; // edit modal has its own handler
      if (!e.clipboardData?.items) return;
      for (const item of e.clipboardData.items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          setProofFile(file);
          setProofPreview(URL.createObjectURL(file));
          toast.success('Screenshot captured as proof');
          break;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [editModal]);

  // Ctrl+V paste inside edit modal
  useEffect(() => {
    if (!editModal) return;
    const handlePaste = e => {
      if (!e.clipboardData?.items) return;
      for (const item of e.clipboardData.items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          setEditProofFile(file);
          setEditProofPreview(URL.createObjectURL(file));
          setEditRemoveProof(false);
          toast.success('Screenshot captured as proof');
          break;
        }
      }
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [editModal]);

  const { data: clients=[] } = useQuery({ queryKey:['clients'], queryFn:()=>api.get('/clients').then(r=>r.data) });
  const { data: categories=[], isLoading: catsLoading } = useQuery({
    queryKey:['categories', form.client_id],
    queryFn:()=>api.get(`/categories${form.client_id?'?clientId='+form.client_id:''}`).then(r=>r.data),
  });
  const { data: myEntries=[] } = useQuery({
    queryKey:['myEntries', month],
    queryFn:()=>api.get(`/entries?month=${month}`).then(r=>r.data.filter(e=>e.logged_by===user.id)),
  });
  const { data: editCategories=[] } = useQuery({
    queryKey:['categories', editClientId],
    queryFn:()=>api.get(`/categories?clientId=${editClientId}`).then(r=>r.data),
    enabled:!!editClientId,
  });
  const { data: corrTypes=[] } = useQuery({ queryKey:['correction-types'], queryFn:()=>api.get('/correction-types').then(r=>r.data) });
  const corrMutation = useMutation({
    mutationFn: ({id,...d}) => api.patch(`/entries/${id}/correction`,d),
    onSuccess: () => { toast.success('Corrections updated'); qc.invalidateQueries(['myEntries']); qc.invalidateQueries(['entries']); setCorrModal(null); },
    onError: err => toast.error(err.response?.data?.error || 'Failed to update corrections'),
  });

  const selectedClient = clients.find(c=>c.id===form.client_id);
  const nodes = selectedClient?.allNodes || [];
  const globalCats = categories.filter(c=>!c.client_id);
  const clientCats = categories.filter(c=>c.client_id);

  // Person→Stakeholder cascade state
  const [personNodeId, setPersonNodeId] = useState('');
  const topNodes = nodes.filter(n=>!n.parent_id); // Person level
  const stakeholderNodes = personNodeId ? nodes.filter(n=>n.parent_id===personNodeId) : [];

  useEffect(()=>{ setForm(f=>({...f,category_id:'',hierarchy_node_id:''})); setPersonNodeId(''); setDup(null); },[form.client_id]);

  function handlePersonSelect(pid) {
    setPersonNodeId(pid);
    const children = nodes.filter(n=>n.parent_id===pid);
    // If person has no children, tag the entry directly to the person
    setForm(f=>({...f, hierarchy_node_id: children.length===0 ? pid : ''}));
  }

  useEffect(()=>{
    if(!form.client_id||!form.category_id||form.description.length<3){setDup(null);return;}
    const t=setTimeout(async()=>{
      try{
        const r=await api.get('/entries/check-duplicate',{params:{clientId:form.client_id,categoryId:form.category_id,description:form.description,month:form.work_date.slice(0,7)}});
        setDup(r.data);
      }catch{}
    },500);
    return()=>clearTimeout(t);
  },[form.client_id,form.category_id,form.description,form.work_date]);

  const submit = useMutation({
    mutationFn: d => api.post('/entries', d),
    onSuccess: async res => {
      // Upload proof image if one was selected
      if (proofFile) {
        try {
          const fd = new FormData();
          fd.append('proof', proofFile);
          await api.post(`/entries/${res.data.id}/proof`, fd);
        } catch { toast.error('Entry saved but proof upload failed — try attaching from the Work Log.'); }
      }
      toast.success('Entry saved!');
      setForm({ client_id:'', category_id:'', hierarchy_node_id:'', description:'', quantity:1, work_date:today, is_billable:true });
      setPersonNodeId('');
      setDup(null);
      setProofFile(null);
      setProofPreview(null);
      if (proofInputRef.current) proofInputRef.current.value = '';
      qc.invalidateQueries(['myEntries']);
      qc.invalidateQueries(['entries']);
      qc.invalidateQueries(['dashboard']);
      if(isMobile) setStep(1);
    },
    onError: err => toast.error(err.response?.data?.error || 'Failed to save entry — please try again'),
  });

  function buildNodeOptions(nodes, parentId=null, depth=0) {
    return nodes.filter(n=>n.parent_id===parentId).flatMap(n=>[
      <option key={n.id} value={n.id}>{' '.repeat(depth*4)}{n.label}: {n.name}</option>,
      ...buildNodeOptions(nodes,n.id,depth+1),
    ]);
  }

  const editMutation = useMutation({
    mutationFn: async ({ id, ...data }) => {
      await api.patch(`/entries/${id}`, data);
      if (editRemoveProof && !editProofFile) {
        await api.delete(`/entries/${id}/proof`);
      } else if (editProofFile) {
        const fd = new FormData();
        fd.append('proof', editProofFile);
        await api.post(`/entries/${id}/proof`, fd);
      }
    },
    onSuccess: () => {
      toast.success('Entry updated');
      setEditModal(null);
      setEditClientId('');
      setEditProofFile(null);
      setEditProofPreview(null);
      setEditRemoveProof(false);
      qc.invalidateQueries(['myEntries']);
      qc.invalidateQueries(['entries']);
    },
    onError: err => toast.error(err.response?.data?.error || 'Update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: id => api.delete(`/entries/${id}`),
    onSuccess: () => { toast.success('Entry deleted'); setDeleteConfirm(null); qc.invalidateQueries(['myEntries']); qc.invalidateQueries(['entries']); qc.invalidateQueries(['dashboard']); },
    onError: err => toast.error(err.response?.data?.error || 'Failed to delete entry'),
  });

  function openEdit(e) {
    const dateStr = (e.work_date||'').slice(0,10);
    const editClient = clients.find(c=>c.id===e.client_id);
    const allNodes = editClient?.allNodes || [];
    const node = allNodes.find(n=>n.id===e.hierarchy_node_id);
    const personId = node ? (node.parent_id || node.id) : '';
    setEditClientId(e.client_id||'');
    setEditPersonNodeId(personId);
    setEditForm({ description:e.description, quantity:e.quantity, work_date:dateStr, category_id:e.category_id||'', hierarchy_node_id:e.hierarchy_node_id||'', is_billable:e.is_billable!==false });
    setEditProofFile(null);
    setEditProofPreview(e.proof_image ? `/uploads/proofs/${e.proof_image}` : null);
    setEditRemoveProof(false);
    setEditModal(e);
  }

  function clearProof() {
    setProofFile(null);
    setProofPreview(null);
    if (proofInputRef.current) proofInputRef.current.value = '';
  }

  const ProofUploader = () => (
    <div className="field">
      <label>Design proof — paste screenshot (Ctrl+V) or upload <span style={{color:'var(--danger)'}}>*</span></label>
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
        <input
          ref={proofInputRef} type="file" accept="image/*" style={{ display:'none' }}
          onChange={e => {
            const f = e.target.files[0];
            if (f) { setProofFile(f); setProofPreview(URL.createObjectURL(f)); }
          }}
        />
        <button type="button" className="btn-sm" onClick={() => proofInputRef.current?.click()}>
          📎 Select file
        </button>
        <span style={{ fontSize:11, color:'var(--text3)' }}>or Ctrl+V to paste screenshot</span>
        {proofPreview && (
          <>
            <img src={proofPreview} alt="proof preview" style={{ height:48, borderRadius:6, border:'1px solid var(--border)', objectFit:'cover' }} />
            <button type="button" className="btn-sm" onClick={clearProof} style={{ fontSize:11 }}>✕ Remove</button>
          </>
        )}
      </div>
    </div>
  );

  // MOBILE LAYOUT
  if (isMobile) {
    return (
      <div style={{padding:'0 0 80px'}}>
        <div style={{display:'flex',gap:0,marginBottom:16,borderBottom:'2px solid var(--border)'}}>
          {['Client','Category','Details'].map((s,i)=>(
            <div key={s} onClick={()=>setStep(i+1)} style={{flex:1,textAlign:'center',padding:'10px 0',fontWeight:600,fontSize:13,color:step===i+1?'var(--accent)':'var(--text2)',borderBottom:step===i+1?'2px solid var(--accent)':'2px solid transparent',cursor:'pointer'}}>
              {i+1}. {s}
            </div>
          ))}
        </div>

        {step===1 && (
          <div>
            <div style={{fontSize:13,color:'var(--text2)',marginBottom:10}}>Select client</div>
            {clients.map(c=>(
              <button key={c.id} onClick={()=>{setForm(f=>({...f,client_id:c.id}));setStep(2);}} style={{width:'100%',textAlign:'left',padding:'14px 16px',marginBottom:8,borderRadius:10,fontSize:15,fontWeight:500,background:form.client_id===c.id?'var(--accent-light)':'var(--surface)',borderColor:form.client_id===c.id?'var(--accent)':'var(--border)'}}>
                {c.name}
              </button>
            ))}
          </div>
        )}

        {step===2 && (
          <div>
            <div style={{fontSize:13,color:'var(--text2)',marginBottom:10}}>Select category</div>
            {clientCats.length>0 && <><div style={{fontSize:11,color:'var(--text3)',fontWeight:700,marginBottom:6,textTransform:'uppercase'}}>Only for {selectedClient?.name}</div>
            {clientCats.map(c=>(
              <button key={c.id} onClick={()=>{setForm(f=>({...f,category_id:c.id}));setStep(3);}} style={{width:'100%',textAlign:'left',padding:'14px 16px',marginBottom:8,borderRadius:10,fontSize:15,fontWeight:500,background:form.category_id===c.id?'var(--accent-light)':'var(--surface)',borderColor:form.category_id===c.id?'var(--accent)':'var(--border)'}}>
                {c.name}
              </button>
            ))}</>}
            {globalCats.length>0 && <div style={{fontSize:11,color:'var(--text3)',fontWeight:700,margin:'12px 0 6px',textTransform:'uppercase'}}>Global</div>}
            {globalCats.map(c=>(
              <button key={c.id} onClick={()=>{setForm(f=>({...f,category_id:c.id}));setStep(3);}} style={{width:'100%',textAlign:'left',padding:'14px 16px',marginBottom:8,borderRadius:10,fontSize:15,fontWeight:500,background:form.category_id===c.id?'var(--accent-light)':'var(--surface)',borderColor:form.category_id===c.id?'var(--accent)':'var(--border)'}}>
                {c.name}
              </button>
            ))}
          </div>
        )}

        {step===3 && (
          <div>
            {dup?.isDuplicate && <div className="dup-warn" style={{background:'#FDF0EE',borderColor:'var(--red)',marginBottom:12}}>🔴 <strong>Exact duplicate!</strong> Already logged by {dup.existingEntry?.logged_by_name} on {dup.existingEntry?.work_date}.</div>}
            {!dup?.isDuplicate && dup?.similar?.length>0 && <div className="dup-warn" style={{marginBottom:12}}>⚠️ Similar entries exist: {dup.similar.map(s=>`"${s.description}"`).join(', ')}</div>}
            {selectedClient?.type==='hierarchical'&&<>
              <div className="field"><label>Person</label>
                <select value={personNodeId} onChange={e=>handlePersonSelect(e.target.value)} style={{fontSize:15,padding:12}}>
                  <option value="">Select person (optional)</option>
                  {topNodes.map(n=><option key={n.id} value={n.id}>{n.name}</option>)}
                </select>
                <AddNodeInline clientId={form.client_id} label="Person"
                  onAdded={n=>{ setPersonNodeId(n.id); setForm(f=>({...f,hierarchy_node_id:n.id})); }}/>
              </div>
              {personNodeId&&<div className="field"><label>Stakeholder</label>
                {stakeholderNodes.length>0&&<select value={form.hierarchy_node_id} onChange={e=>setForm(f=>({...f,hierarchy_node_id:e.target.value}))} style={{fontSize:15,padding:12}}>
                  <option value={personNodeId}>— None —</option>
                  {stakeholderNodes.map(n=><option key={n.id} value={n.id}>{n.name}</option>)}
                </select>}
                <AddNodeInline clientId={form.client_id} parentId={personNodeId} label="Stakeholder"
                  onAdded={n=>setForm(f=>({...f,hierarchy_node_id:n.id}))}/>
              </div>}
            </>}
            <div className="field"><label>Description</label><textarea rows={3} value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="What did you design?" style={{fontSize:15,padding:12}}/></div>
            <div className="form-grid">
              <div className="field"><label>Date</label><input type="date" value={form.work_date} onChange={e=>setForm(f=>({...f,work_date:e.target.value}))} style={{fontSize:15,padding:12}}/></div>
              <div className="field"><label>Qty</label><input type="number" min="1" value={form.quantity} onChange={e=>setForm(f=>({...f,quantity:parseInt(e.target.value)||1}))} style={{fontSize:15,padding:12}}/></div>
            </div>
            <ProofUploader />
            <button className="btn-primary" onClick={()=>{if(!form.description){toast.error('Add description');return;}if(!proofFile){toast.error('Design proof is required — paste or upload a screenshot');return;}submit.mutate(form);}} disabled={submit.isPending} style={{width:'100%',padding:16,fontSize:16,borderRadius:12,justifyContent:'center',marginTop:8}}>
              {submit.isPending?'Saving…':'✓ Save entry'}
            </button>
          </div>
        )}
      </div>
    );
  }

  // DESKTOP LAYOUT
  return (
    <div>
      <div className="section-head"><h2>Log work</h2></div>
      <div className="card" style={{marginBottom:16}}>
        <div className="card-title">New work entry</div>
        {dup?.isDuplicate && <div className="dup-warn" style={{background:'#FDF0EE',borderColor:'var(--red)'}}>🔴 <strong>Exact duplicate!</strong> Already logged by {dup.existingEntry?.logged_by_name} on {dup.existingEntry?.work_date}.</div>}
        {!dup?.isDuplicate && dup?.similar?.length>0 && <div className="dup-warn">⚠️ <strong>Similar entries:</strong> {dup.similar.map(s=>`"${s.description}" (${s.logged_by_name})`).join(' · ')}</div>}
        <div className="field"><label>Client *</label>
          <SearchableSelect
            value={form.client_id}
            onChange={id => setForm(f => ({ ...f, client_id: id }))}
            items={clients}
            placeholder="Select client"
          />
        </div>
        {selectedClient && (topNodes.length>0 || selectedClient.type==='hierarchical') && (
          <div className="form-grid">
            <div className="field">
              <label>Person</label>
              <select value={personNodeId} onChange={e=>handlePersonSelect(e.target.value)}>
                <option value="">Select person (optional)</option>
                {topNodes.map(n=><option key={n.id} value={n.id}>{n.name}</option>)}
              </select>
              <AddNodeInline clientId={form.client_id} label="Person"
                onAdded={n=>{ setPersonNodeId(n.id); setForm(f=>({...f,hierarchy_node_id:n.id})); }}/>
            </div>
            {personNodeId && (
              <div className="field">
                <label>Stakeholder</label>
                {stakeholderNodes.length>0 && (
                  <select value={form.hierarchy_node_id} onChange={e=>setForm(f=>({...f,hierarchy_node_id:e.target.value}))}>
                    <option value={personNodeId}>— None (log to person only) —</option>
                    {stakeholderNodes.map(n=><option key={n.id} value={n.id}>{n.name}</option>)}
                  </select>
                )}
                <AddNodeInline clientId={form.client_id} parentId={personNodeId} label="Stakeholder"
                  onAdded={n=>setForm(f=>({...f,hierarchy_node_id:n.id}))}/>
              </div>
            )}
          </div>
        )}
        <div className="field"><label>Category *</label>
          <SearchableSelect
            value={form.category_id}
            onChange={id => setForm(f => ({ ...f, category_id: id }))}
            groups={[
              { label: clientCats.length ? `Only for ${selectedClient?.name || 'client'}` : null, items: clientCats },
              { label: clientCats.length ? 'Global' : null, items: globalCats },
            ]}
            disabled={catsLoading}
            placeholder="Select category"
          />
        </div>
        <div className="field"><label>Description / Project Name / File Name *</label><input value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} placeholder="e.g. June newsletter design for summer campaign"/></div>
        <div className="form-grid">
          <div className="field"><label>Date *</label><input type="date" value={form.work_date} onChange={e=>setForm(f=>({...f,work_date:e.target.value}))}/></div>
          <div className="field"><label>Quantity</label><input type="number" min="1" value={form.quantity} onChange={e=>setForm(f=>({...f,quantity:parseInt(e.target.value)||1}))}/></div>
        </div>
        <ProofUploader />
        <div style={{display:'flex',justifyContent:'flex-start',marginTop:8}}>
          <button className="btn-primary" onClick={()=>{if(!form.client_id||!form.category_id||!form.description){toast.error('Fill required fields: client, category, and description');return;}if(!proofFile){toast.error('Design proof is required — paste or upload a screenshot');return;}submit.mutate(form);}} disabled={submit.isPending}>
            {submit.isPending?'Saving…':'✓ Save entry'}
          </button>
        </div>
      </div>

      <div className="section-head"><h2>My entries — {new Date(month+'-01').toLocaleString('default',{month:'long',year:'numeric'})}</h2></div>
      <div className="tbl-wrap"><table>
        <thead><tr><th>Date</th><th>Client</th><th>Category</th><th>Description</th><th>Qty</th><th>Billing</th><th>Proof</th><th></th></tr></thead>
        <tbody>
          {myEntries.slice(0,15).map(e=>(
            <tr key={e.id} className={e.corrections>0?'cor-row':''}>
              <td style={{color:'var(--text2)',whiteSpace:'nowrap'}}>{fmtDate(e.work_date)}</td>
              <td><span className="pill pill-blue" style={{fontSize:10}}>{e.client_name}</span></td>
              <td><span className="pill pill-gray">{e.category_name}</span></td>
              <td>{e.description}{e.corrections>0&&<span className="cor-tag" style={{marginLeft:6}}>+{e.corrections} cor</span>}</td>
              <td>{e.quantity}</td>
              <td><span style={{fontSize:11,fontWeight:600,padding:'2px 8px',borderRadius:10,background:e.is_billable!==false?'#DCFCE7':'#F3F4F6',color:e.is_billable!==false?'#15803D':'#6B7280'}}>{e.is_billable!==false?'Billable':'Non-bill'}</span></td>
              <td>{e.proof_image
                ? <img src={`/uploads/proofs/${e.proof_image}`} alt="proof" style={{height:32,width:32,objectFit:'cover',borderRadius:4,border:'1px solid var(--border)',cursor:'pointer'}} onClick={()=>setPreviewImg(`/uploads/proofs/${e.proof_image}`)} />
                : <span style={{fontSize:11,color:'var(--text3)'}}>—</span>
              }</td>
              <td style={{whiteSpace:'nowrap'}}>
                <button className="btn-sm" style={{marginRight:4}} onClick={()=>{
                  const existing=(e.correction_lines||[]);
                  setCorrLines(existing.length?existing.map(l=>({correction_type_id:l.correction_type_id||'',correction_count:l.count||1,reason:l.reason||''})):[{correction_type_id:'',correction_count:e.corrections||1,reason:e.correction_reason||''}]);
                  setCorrModal(e);
                }}>Manage corrections</button>
                <button className="btn-sm" onClick={()=>openEdit(e)} style={{marginRight:4}}>Edit</button>
                <button className="btn-danger btn-sm" onClick={()=>setDeleteConfirm(e.id)}>🗑</button>
              </td>
            </tr>
          ))}
          {!myEntries.length&&<tr><td colSpan={8}><div className="empty">No entries for this month yet</div></td></tr>}
        </tbody>
      </table></div>

      {previewImg && (
        <div className="modal-overlay" onClick={()=>setPreviewImg(null)} style={{background:'rgba(0,0,0,0.85)',cursor:'zoom-out'}}>
          <img src={previewImg} alt="Design proof" style={{maxWidth:'90vw',maxHeight:'90vh',borderRadius:8,boxShadow:'0 8px 40px rgba(0,0,0,0.5)'}} onClick={ev=>ev.stopPropagation()}/>
          <button style={{position:'fixed',top:16,right:16,background:'rgba(255,255,255,0.15)',color:'#fff',border:'none',borderRadius:8,padding:'6px 14px',fontSize:14,cursor:'pointer'}} onClick={()=>setPreviewImg(null)}>✕ Close</button>
        </div>
      )}

      {editModal && (() => {
        const editClient = clients.find(c=>c.id===editClientId);
        const allNodes = editClient?.allNodes || [];
        const topNodes = allNodes.filter(n=>!n.parent_id);
        const stakeholderNodes = editPersonNodeId ? allNodes.filter(n=>n.parent_id===editPersonNodeId) : [];
        return (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-title">Edit entry</div>
              <div className="field">
                <label>Client</label>
                <SearchableSelect
                  value={editClientId}
                  onChange={id => {
                    setEditClientId(id);
                    setEditPersonNodeId('');
                    // Category is left as-is — hierarchy nodes are client-specific so those reset,
                    // but the category shouldn't silently change unless the user picks a new one.
                    setEditForm(f => ({ ...f, hierarchy_node_id: '' }));
                  }}
                  items={clients}
                  placeholder="Select client"
                />
              </div>
              <div className="field">
                <label>Category</label>
                <SearchableSelect
                  value={editForm.category_id}
                  onChange={id => setEditForm(f => ({ ...f, category_id: id }))}
                  groups={[
                    { label: editClient ? `Only for ${editClient.name}` : null, items: editCategories.filter(c => c.client_id) },
                    { label: editClient ? 'Global' : null, items: editCategories.filter(c => !c.client_id) },
                  ]}
                  placeholder="Select category"
                />
              </div>
              {topNodes.length>0&&(
                <div className="form-grid">
                  <div className="field">
                    <label>Person</label>
                    <select value={editPersonNodeId} onChange={ev=>{
                      const pid=ev.target.value;
                      setEditPersonNodeId(pid);
                      const children=allNodes.filter(n=>n.parent_id===pid);
                      setEditForm(f=>({...f,hierarchy_node_id:children.length===0?pid:''}));
                    }}>
                      <option value="">— None —</option>
                      {topNodes.map(n=><option key={n.id} value={n.id}>{n.name}</option>)}
                    </select>
                  </div>
                  {editPersonNodeId&&stakeholderNodes.length>0&&(
                    <div className="field">
                      <label>Stakeholder</label>
                      <select value={editForm.hierarchy_node_id} onChange={ev=>setEditForm(f=>({...f,hierarchy_node_id:ev.target.value}))}>
                        <option value={editPersonNodeId}>— None (person only) —</option>
                        {stakeholderNodes.map(n=><option key={n.id} value={n.id}>{n.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}
              <div className="field">
                <label>Description</label>
                <input value={editForm.description} onChange={ev=>setEditForm(f=>({...f,description:ev.target.value}))} placeholder="Description"/>
              </div>
              <div className="form-grid">
                <div className="field">
                  <label>Date</label>
                  <input type="date" value={editForm.work_date} onChange={ev=>setEditForm(f=>({...f,work_date:ev.target.value}))}/>
                </div>
                <div className="field">
                  <label>Quantity</label>
                  <input type="number" min="1" value={editForm.quantity} onChange={ev=>setEditForm(f=>({...f,quantity:parseInt(ev.target.value)||1}))}/>
                </div>
              </div>
              <div className="field">
                <label>Design proof — paste screenshot (Ctrl+V), upload, or remove</label>
                <input ref={editProofInputRef} type="file" accept="image/*" style={{display:'none'}}
                  onChange={ev=>{const f=ev.target.files[0];if(f){setEditProofFile(f);setEditProofPreview(URL.createObjectURL(f));setEditRemoveProof(false);}}}/>
                <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                  <button type="button" className="btn-sm" onClick={()=>editProofInputRef.current?.click()}>📎 Upload image</button>
                  <span style={{fontSize:11,color:'var(--text3)'}}>or Ctrl+V to paste</span>
                  {editProofPreview && !editRemoveProof && (
                    <>
                      <img src={editProofPreview} alt="proof" style={{height:48,borderRadius:6,border:'1px solid var(--border)',objectFit:'cover',cursor:'pointer'}} onClick={()=>setPreviewImg(editProofPreview)}/>
                      <span style={{fontSize:11,color:'var(--text2)'}}>{editProofFile ? 'New image selected' : 'Current proof'}</span>
                      <button type="button" className="btn-danger btn-sm" onClick={()=>{setEditRemoveProof(true);setEditProofFile(null);setEditProofPreview(null);}}>✕ Remove</button>
                    </>
                  )}
                  {editRemoveProof && <span style={{fontSize:11,color:'var(--danger)'}}>Proof will be removed on save</span>}
                  {!editProofPreview && !editRemoveProof && <span style={{fontSize:11,color:'var(--text2)'}}>No proof attached</span>}
                </div>
              </div>
              <div className="modal-actions">
                <button onClick={()=>setEditModal(null)}>Cancel</button>
                <button className="btn-primary" onClick={()=>{
                  if(!editForm.description){toast.error('Description cannot be empty');return;}
                  editMutation.mutate({id:editModal.id, client_id:editClientId, ...editForm});
                }} disabled={editMutation.isPending}>
                  {editMutation.isPending?'Saving…':'Save changes'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {corrModal&&(
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:480}}>
            <div className="modal-title">Manage corrections</div>
            <div style={{fontSize:13,color:'var(--text2)',marginBottom:14,padding:10,background:'var(--surface2)',borderRadius:'var(--radius-sm)'}}>
              <strong>{corrModal.client_name}</strong> — {corrModal.category_name}: {corrModal.description}
            </div>
            <div style={{fontSize:11,fontWeight:600,color:'var(--text3)',marginBottom:8,textTransform:'uppercase',letterSpacing:'0.05em'}}>Correction lines</div>
            {corrLines.map((line,i)=>(
              <div key={i} style={{border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:'10px 12px',marginBottom:8,background:'var(--surface2)'}}>
                <div style={{display:'flex',gap:8,alignItems:'flex-end',flexWrap:'wrap'}}>
                  <div style={{flex:'1 1 160px'}}>
                    <div style={{fontSize:11,color:'var(--text3)',marginBottom:4}}>Correction type</div>
                    <select value={line.correction_type_id} onChange={ev=>{const v=[...corrLines];v[i]={...v[i],correction_type_id:ev.target.value};setCorrLines(v);}}>
                      <option value="">— Untyped —</option>
                      {corrTypes.map(t=><option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                  <div style={{width:70}}>
                    <div style={{fontSize:11,color:'var(--text3)',marginBottom:4}}>Count</div>
                    <input type="number" min="1" value={line.correction_count} onChange={ev=>{const v=[...corrLines];v[i]={...v[i],correction_count:parseInt(ev.target.value)||1};setCorrLines(v);}}/>
                  </div>
                  {corrLines.length>1&&<button className="btn-danger btn-sm" style={{marginBottom:2}} onClick={()=>setCorrLines(corrLines.filter((_,j)=>j!==i))}>✕</button>}
                </div>
                <div style={{marginTop:8}}>
                  <div style={{fontSize:11,color:'var(--text3)',marginBottom:4}}>Reason (optional)</div>
                  <input value={line.reason} onChange={ev=>{const v=[...corrLines];v[i]={...v[i],reason:ev.target.value};setCorrLines(v);}} placeholder="e.g. Client requested colour change"/>
                </div>
              </div>
            ))}
            <button className="btn-sm" style={{marginBottom:12}} onClick={()=>setCorrLines([...corrLines,{correction_type_id:'',correction_count:1,reason:''}])}>+ Add another correction type</button>
            <div className="modal-actions">
              <button onClick={()=>setCorrModal(null)}>Cancel</button>
              <button className="btn-primary" onClick={()=>corrMutation.mutate({id:corrModal.id,correctionLines:corrLines})} disabled={corrMutation.isPending}>{corrMutation.isPending?'Saving…':'Apply'}</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteConfirm}
        title="Delete entry?"
        message="This will permanently remove the entry and cannot be undone."
        confirmLabel="Delete"
        onConfirm={() => deleteMutation.mutate(deleteConfirm)}
        onCancel={() => setDeleteConfirm(null)}
      />
    </div>
  );
}
